<template>
  <div class="contact-basic">

    <div class="info-row"
         v-if="contact.email">
      <a target="_top" :href="`mailto:${contact.email}`">
        <v-icon class="info-row_icon">
          icon-envelop-fill-16
        </v-icon>
      </a>
      <div class="info-row_text">
        <a target="_top" :href="`mailto:${contact.email}`">
          <div class="info-row_text-label">
            {{fieldLabels.email}}
          </div>
        </a>
        <div class="tooltips-wrapper">
          <v-tooltip top
                     content-class="details-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <div class="info-row_text-value"
                   :class="{ 'with-error': contact.blocked }"
                   v-bind="attrs"
                   v-on="on">
                {{contact.email}}
              </div>
            </template>
            <span>
            {{contact.email}}
          </span>
          </v-tooltip>

          <v-tooltip bottom
                     content-class="warning-tooltip"
                     v-if="contact.blocked">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="warning-icon"
                      v-bind="attrs"
                      v-on="on">
                icon-warning
              </v-icon>
            </template>
            <div class="warning-tooltip-wrapper">
              <div class="warning-tooltip-header">
                {{$t('client.matters.matter_page.contact.blocked_email_title')}}
              </div>
              <div class="warning-tooltip-message">
                {{$t('client.matters.matter_page.contact.blocked_email_message')}}
              </div>
            </div>
          </v-tooltip>

        </div>
      </div>
      <div class="copy-filed__container">
        <CopyBtn :copy-text="contact.email"
                    :tooltip-copied="$t('client.matters.matter_page.contact.copied')"
                    :tooltip-copy="$t('client.matters.matter_page.contact.copymail')"/>
      </div>
    </div>

    <div class="info-row"
         v-if="contact.notifications_phone && contact.notifications_phone.value">
      <a target="_top" :href="`tel:${contact.notifications_phone.display_value}`">
        <v-icon class="info-row_icon">
          icon-Temp_phone-fill-16_solid
        </v-icon>
      </a>
      <div class="info-row_text">
        <a target="_top" :href="`tel:${contact.notifications_phone.display_value}`">
          <div class="info-row_text-label">
            {{fieldLabels.phone}}
          </div>
        </a>
        <div class="tooltips-wrapper">

          <v-tooltip top
                     content-class="details-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <div class="info-row_text-value"
                   :class="{ 'with-error': invalidMobileNumber }"
                   v-bind="attrs"
                   v-on="on">
                {{contact.notifications_phone.display_value}}
              </div>
            </template>
            <span>
            {{contact.notifications_phone.display_value}}
            </span>
          </v-tooltip>

          <v-tooltip bottom
                     content-class="warning-tooltip"
                     v-if="invalidMobileNumber">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="warning-icon"
                      v-bind="attrs"
                      v-on="on">
                icon-warning
              </v-icon>
            </template>
            <div class="warning-tooltip-wrapper">
              <div class="warning-tooltip-header">
                {{$t('client.matters.matter_page.contact.invalid_phone_title')}}
              </div>
              <div class="warning-tooltip-message">
                {{$t('client.matters.matter_page.contact.invalid_phone_message')}}
              </div>
            </div>
          </v-tooltip>

        </div>
      </div>
      <div class="copy-filed__container">
        <CopyBtn :copy-text="contact.notifications_phone.display_value"
                    :tooltip-copied="$t('client.matters.matter_page.contact.copied')"
                    :tooltip-copy="$t('client.matters.matter_page.contact.copyphone')"/>
      </div>
    </div>

    <div class="info-row"
         v-if="contact.address">
      <v-icon class="info-row_icon"
              @click="addressClick">
        icon-Temp_address_solid
      </v-icon>
      <div class="info-row_text">
        <div class="info-row_text-label"
             @click="addressClick">
          {{fieldLabels.address}}
        </div>

        <v-tooltip top
                   content-class="details-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <div class="info-row_text-value"
                 v-bind="attrs"
                 v-on="on">
              {{contact.address}}
            </div>
          </template>
          <span>
            {{contact.address}}
          </span>
        </v-tooltip>

      </div>
    </div>

    <v-icon class="social-media-icon"
            v-if="contact.facebook_profile"
            @click="socialMediaClick(contact.facebook_profile)">
      icon-facebook
    </v-icon>

    <v-icon class="social-media-icon"
            v-if="contact.twitter_profile"
            @click="socialMediaClick(contact.twitter_profile)">
      icon-twitter
    </v-icon>

    <v-icon class="social-media-icon linkedin-icon"
            v-if="contact.linkedin_profile"
            @click="socialMediaClick(contact.linkedin_profile)">
      icon-linkedin
    </v-icon>

  </div>
</template>

<script>
  import {urlUtil} from "@/modules/common/utils";
  import CopyBtn from "@/modules/common/components/CopyBtn/CopyBtn";

  export default {
    name: "ContactBasic",
    components: {CopyBtn},
    props: {
      contact: {
        type: Object,
        required: true
      },
      fieldLabels: {
        type: Object,
        required: true
      }
    },
    computed: {
      invalidMobileNumber() {
        return this.contact.notifications_phone && this.contact.notifications_phone.value && !this.contact.is_notifications_phone_mobile;
      }
    },
    methods: {
      addressClick() {
        this.$frontageApi.openExternal(`https://www.google.com/maps/place/${this.contact.address}`);
      },
      socialMediaClick(link) {
        const url = urlUtil.isUrl(link) ? link : `https://${link}`;
        this.$frontageApi.openExternal(url);
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .contact-basic {

    .v-icon {
      transition: none;
    }

    .info-row {
      height: 40px;
      margin: 6px 0;
      display: flex;

      &:hover {
        .info-row {

          &_text {
            width: calc(100% - 64px);
          }
        }
      }

      &__copy {
        margin-block-start: 5px;

        i {
          font-size: 16px;
        }

        &--success {
          color: $green-color-2;
        }
      }

      &_icon {
        font-size: 16px;
        color: $navy-color-1;
        border: 1px solid $grey-color-2;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        cursor: pointer;
        position: relative;
        top: 1px;
        margin-inline-end: 10px;
      }

      &_text {

        width: calc(100% - 42px);
        @include md-and-down {
          width: calc(100% - 64px);
        }

        .warning-icon {
          font-size: 13px;
          color: $red-color-1;
          padding: 0 5px;
          @include i-float(left);
          top: 2px;
        }

        &-label {
          font-size: 11px;
          font-weight: 600;
          color: $blue-color-4;
          cursor: pointer;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &-value {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: default;
          max-width: 100%;

          &.with-error {
            max-width: calc(100% - 25px);
          }
        }

        .tooltips-wrapper {

          .info-row_text-value {
            @include i-float(left);
          }
        }
      }
      .copy-filed__container {
        display: none;

        @include md-and-down {
          display: block;
        }
      }
      &:hover .copy-filed__container {
        display: block;
      }
    }

    .social-media-icon {
      color: $navy-color-1;
      border: 0;
      margin: 10px 10px 5px 0;
      font-size: 13px;
      border-radius: 50% !important;
      background-color: $grey-color-11;
      height: 20px;
      width: 20px;

      &:not(.linkedin-icon) {
        padding-top: 2px;
      }

      &.linkedin-icon {
        padding-left: 2px;
      }
    }
  }

  .warning-tooltip {
    background-color: white;
    padding: 0;
    width: 250px;
    height: 100px;
    @include i-position-mirror(left, auto, '!');
    @include i-position-mirror(right, 6px, '!');
    box-shadow: 0 2px 5px 1px rgba(18, 52, 77, 0.2), 0 12px 6px 7px rgba(18, 53, 77, 0.04);
    opacity: 1 !important;
    pointer-events: auto;
    z-index: 50 !important;
  }

  .warning-tooltip-wrapper {
    font-family: var(--primary-font-family);

    .warning-tooltip-header {
      color: $red-color-1;
      background-color: $red-color-2;
      font-size: 12px;
      font-weight: 600;
      padding: 10px;
    }

    .warning-tooltip-message {
      font-size: 11px;
      color: $blue-color-2;
      font-weight: 500;
      line-height: normal;
      padding: 10px 12px;
    }
  }

  .details-tooltip {
    font-family: var(--primary-font-family);
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    z-index: 50 !important;
  }

  a {
    text-decoration: none;
  }
</style>
